import React, {
    useState,
    useEffect,
    useImperativeHandle,
    forwardRef
} from 'react';
import PropTypes from 'prop-types';
import {
    isEqual as _isEqual,
    cloneDeep as _cloneDeep
} from 'lodash';

import { makeStyles } from '@mui/styles';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormLabel from '@mui/material/FormLabel';
import Checkbox from '@mui/material/Checkbox';

import Validator, { useValidate, checkValidity } from '../validator';

import SystemService from '../../data/system-service';

const useStyles = makeStyles(theme => ({
    fullWidth: {
        width: '100%',
    },
    timezone: {
        width: '120px',
    },
    hideFileInput: {
        display: 'none',
    },
    buttonRight: {
        float: 'right',
        marginLeft: theme.spacing(2),
    },
    certBox: {
        marginTop: theme.spacing(2),
        padding: theme.spacing(2),
        border: `1px solid ${theme.palette.primary.main}`,
        background: theme.styled.paper.background,
        color: theme.palette.text.primary,
    },
}));

const Netflow = forwardRef((props, ref) => {
    const classes = useStyles();
    const [init, setInit] = useState(true);
    const [original, setOriginal] = useState(_cloneDeep(props.data));
    const [data, setData] = useState(_cloneDeep(props.data));
    const [, setChanges] = useState({});
    const dataForm = useValidate({});

    useImperativeHandle(ref, () => ({
        getData: () => {
            return data;
        },
    }));

    useEffect(() => {
        if (init) {
            setOriginal(_cloneDeep(props.data));
            setData(_cloneDeep(props.data));
            setInit(false);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props]);

    useEffect(() => {
        const t = !_isEqual(data, original);
        setChanges(t);
        if (t) {
            props.onChange(props.name, t, data);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data]);

    useEffect(() => {
        if (props.onUpdateForm) {
            props.onUpdateForm(dataForm);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dataForm]);

    const handleHost = (e, validID, valid) => {
        dataForm.set({ ...dataForm.get(), [validID]: valid });

        const d = { ...data };
        d.dest.host = e.target.value;
        setData(d);
    };

    const handlePort = (e, validID, valid) => {
        dataForm.set({ ...dataForm.get(), [validID]: valid });

        const d = { ...data };
        d.dest.port = e.target.value;
        setData(d);
    };

    const handleProtocol = (e, component) => {
        const d = { ...data };
        d.dest.proto = component.props.value;
        setData(d);
    };

    const handleCACert = (e) => {
        const d = { ...data };
        d.tls_cert.enable = e.currentTarget.checked;
        setData(d);
    };

    const fileHandler = (file) => {
        const reader = new FileReader();
        return new Promise((resolve, reject) => {
            reader.onload = event => resolve(event.target.result);
            reader.onerror = error => reject(error);
            reader.readAsText(file);
        });
    };

    const handleTLSContents = (e) => {
        fileHandler(e.target.files[0]).then(content => {
            SystemService.exportsCertAttributes(content).then((res) => {
                const d = { ...data };
                d.tls_cert.contents = content;
                d.tls_cert.file_name = `${res.subject} (${res.enddate})`;
                setData(d);
            }, (err) => {
                console.error(err);
            });
        }).catch(err => console.error(err));
    };

    const handleFIPS = (e) => {
        const d = { ...data };
        d.tls_fips = e.currentTarget.checked;
        setData(d);
    };

    const handleVerify = (k, i, close) => {
        setInit(true);
        props.onVerify(k, i, close);
    };

    const handleCancel = (k, i) => {
        setInit(true);
        setData(original);
        props.onCancel(k, i);
    };

    const handleUpdateFrameSep = () => {
        const d = { ...data };
        d.frame_sep_nl = !d.frame_sep_nl;
        setData(d);
    };

    return (
        <Grid container spacing={2}>
            <Grid item xs={6}>
                <Box>
                    <FormControl className={classes.fullWidth}>
                        <FormLabel id="netflow-protocol-label">Protocol</FormLabel>
                        <Select disabled={(!props.perms.write || props.policySource === 'mc2')} id="netflow-protocol" value={data.dest.proto} onChange={handleProtocol}>
                            <MenuItem value="UDP">UDP</MenuItem>
                            <MenuItem value="TCP">TCP</MenuItem>
                            <MenuItem value="TLS">TLS</MenuItem>
                        </Select>
                    </FormControl>
                </Box>
                <Box mt={3}>
                    <FormControl className={classes.fullWidth}>
                        <FormLabel id="netflow-host-label">Hostname</FormLabel>
                        <Validator
                            disabled={(!props.perms.write || props.policySource === 'mc2')}
                            valueType="host"
                            errorText="Must be a valid host"
                            fullWidth
                            value={data.dest.host}
                            onChange={handleHost}
                        />
                    </FormControl>
                </Box>
                <Box mt={3}>
                    <FormControl className={classes.fullWidth}>
                        <FormLabel id="netflow-ip-label">Port</FormLabel>
                        <Validator
                            disabled={(!props.perms.write || props.policySource === 'mc2')}
                            valueType="port"
                            errorText="Must be a valid port number"
                            fullWidth
                            value={data.dest.port}
                            onChange={handlePort}
                        />
                    </FormControl>
                </Box>
            </Grid>
            <Grid item xs={6}>
                <Box>
                    <Typography>Frame Separator</Typography>
                    <FormControlLabel
                        labelPlacement="start"
                        label="New Line"
                        control={
                            <Checkbox
                                checked={data.frame_sep_nl}
                                onClick={handleUpdateFrameSep}
                            />
                        }
                    />
                </Box>
                <Box mt={3}>
                    <FormControlLabel
                        labelPlacement="start"
                        label="FIPS"
                        disabled={(!props.perms.write || props.policySource === 'mc2') || data.dest.proto !== 'TLS'}
                        control={
                            <Checkbox
                                checked={data.tls_fips}
                                name=""
                                onChange={handleFIPS}
                            />
                        }
                    />
                </Box>
                <Box mt={3}>
                    <FormControlLabel
                        labelPlacement="start"
                        label="CA Cert"
                        disabled={(!props.perms.write || props.policySource === 'mc2') || data.dest.proto !== 'TLS'}
                        control={
                            <Checkbox
                                checked={data.tls_cert.enable}
                                name=""
                                onChange={handleCACert}
                            />
                        }
                    />
                </Box>
                <Box mt={3}>
                    <input
                        className={classes.hideFileInput}
                        id={'netflow-cert-file-' + props.index}
                        type="file"
                        onChange={handleTLSContents}
                        disabled={(!props.perms.write || props.policySource === 'mc2') || !data.tls_cert.enable}
                    />
                    {(props.perms.write || props.policySource !== 'mc2') && <label htmlFor={'netflow-cert-file-' + props.index}>
                        <Button
                            variant="contained"
                            color="primary"
                            component="span"
                            aria-label="upload cert"
                            disabled={!data.tls_cert.enable}
                        >
                            Upload Cert
                        </Button>
                    </label>}
                </Box>
                <Box className={classes.certBox} display={data.tls_cert.file_name ? 'flex' : 'none'}>
                    {data.tls_cert.file_name}
                </Box>
            </Grid>
            {(props.perms.write || props.policySource !== 'mc2') && <Grid item xs={12}>
                <Box mt={2}>
                    <Button
                        className={classes.buttonRight}
                        variant="contained"
                        color="primary"
                        aria-label="save"
                        onClick={() => { handleVerify(props.name, props.index, true) }}
                        disabled={!(checkValidity(dataForm.get()))}
                    >Verify</Button>
                    <Button className={classes.buttonRight} variant="contained" color="secondary" aria-label="cancel" onClick={() => { handleCancel(props.name, props.index) }}>Cancel</Button>
                </Box>
            </Grid>}
        </Grid>
    );
});

Netflow.propTypes = {
    data: PropTypes.object.isRequired,
    policySource: PropTypes.string,
    name: PropTypes.string.isRequired,
    index: PropTypes.number.isRequired,
    onCancel: PropTypes.func.isRequired,
    onVerify: PropTypes.func.isRequired,
    onChange: PropTypes.func.isRequired,
    onUpdateForm: PropTypes.func,
    perms: PropTypes.object,
};

export default Netflow;
